import React from 'react';
import { MarketingShell } from '@/seo/kit';

const PrivacyPolicy = () => {
  return (
    <MarketingShell>
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <h1 className="text-4xl md:text-5xl" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>DRAWREADY — PRIVACY POLICY</h1>
          <p className="mt-3" style={{ color: '#5B6678' }}>Effective Date: January 1, 2026</p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-8">

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              DrawReady ("DrawReady," "we," "our," or "us") respects your privacy and is committed to protecting your information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you access or use our web-based software platform (the "Platform").
            </p>
            <p className="text-slate-600 leading-relaxed">
              By using DrawReady, you acknowledge that you have read and understand this Privacy Policy and consent to the practices described herein.
            </p>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">2. Information We Collect</h2>
            
            <div className="border border-slate-200 rounded-md p-5">
              <h3 className="font-semibold text-slate-900 mb-3">A. Information You Provide</h3>
              <p className="text-slate-600 mb-3">When you create an account or use the Platform, we may collect:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Company name and business information</li>
                <li>Project details and metadata</li>
                <li>Budgets, cost data, and financial records</li>
                <li>Uploaded documents (e.g., invoices, receipts, reports)</li>
                <li>Communications sent to DrawReady support</li>
              </ul>
              <p className="text-slate-600 mt-3">
                You are responsible for ensuring that information you submit is accurate and lawful.
              </p>
            </div>

            <div className="border border-slate-200 rounded-md p-5">
              <h3 className="font-semibold text-slate-900 mb-3">B. Information Automatically Collected</h3>
              <p className="text-slate-600 mb-3">When you access the Platform, we may automatically collect:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device and operating system information</li>
                <li>Usage data (pages viewed, features accessed, timestamps)</li>
                <li>Log files and diagnostic data</li>
              </ul>
              <p className="text-slate-600 mt-3">
                This information is used to maintain platform performance, security, and reliability.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed">We use collected information to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide, operate, and maintain the Platform</li>
              <li>Store, organize, and display project data</li>
              <li>Generate reports and standardized documentation</li>
              <li>Process subscription payments and billing</li>
              <li>Communicate with you regarding your account or support requests</li>
              <li>Monitor usage for security, fraud prevention, and compliance</li>
              <li>Improve platform functionality and user experience</li>
            </ul>
            <p className="text-slate-600 leading-relaxed font-medium mt-4">
              We do not use your data for consumer advertising or behavioral marketing.
            </p>
          </section>

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">4. What We Do Not Do</h2>
            <div className="border border-slate-200 rounded-md p-5">
              <p className="text-slate-700 mb-3 font-medium">To be clear:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                <li>We do not sell your personal or business data</li>
                <li>We do not automatically share your data with lenders</li>
                <li>We do not evaluate personal credit or underwriting risk</li>
                <li>We do not use your data for third-party advertising</li>
              </ul>
              <p className="text-slate-700 mt-3 font-medium">
                Any data sharing with lenders occurs only at your direction.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">5. Sharing of Information</h2>
            <p className="text-slate-600 leading-relaxed">
              We may share information with trusted third-party service providers solely to operate the Platform, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Payment processors (e.g., Stripe)</li>
              <li>Cloud hosting and infrastructure providers</li>
              <li>Security, monitoring, and analytics services</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              These providers are contractually obligated to safeguard your data and may only use it to perform services on our behalf.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We may also disclose information if required to comply with legal obligations, enforce our Terms of Service, or protect the rights and safety of DrawReady or others.
            </p>
          </section>

          {/* Section 6 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">6. Data Ownership & Control</h2>
            <p className="text-slate-600 leading-relaxed font-medium">
              You retain ownership of all data you upload to the Platform.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By using DrawReady, you grant us a limited, non-exclusive license to host, process, and display your data solely for the purpose of operating and improving the Platform.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We may use aggregated and anonymized data for analytics, benchmarking, and product improvement.
            </p>
          </section>

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">7. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement reasonable administrative, technical, and organizational safeguards designed to protect your information.
            </p>
            <p className="text-slate-600 leading-relaxed">
              However, no system is completely secure. You acknowledge that DrawReady cannot guarantee absolute security and that use of the Platform is at your own risk.
            </p>
          </section>

          {/* Section 8 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">8. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your information only for as long as necessary to provide the Platform, comply with legal obligations, resolve disputes, and enforce agreements.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Upon account termination, data may be deleted or archived in accordance with operational and legal requirements.
            </p>
          </section>

          {/* Section 9 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">9. Your Privacy Rights</h2>
            <p className="text-slate-600 leading-relaxed">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Access the information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of data, subject to legal and operational limitations</li>
              <li>Request information about how your data is used</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Requests may be submitted by contacting us at the email address listed below.
            </p>
          </section>

          {/* Section 10 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">10. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              DrawReady is intended for business use only and is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors.
            </p>
          </section>

          {/* Section 11 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. When we do, we will revise the effective date above.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Continued use of the Platform after changes become effective constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Section 12 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">12. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              This Privacy Policy is governed by the laws of the State of Wyoming, without regard to conflict-of-law principles.
            </p>
          </section>

          {/* Section 13 */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">13. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, contact:
            </p>
            <p className="text-blue-600 font-medium">
              <a href="mailto:support@drawready.com">support@drawready.com</a>
            </p>
          </section>

        </div>
    </MarketingShell>
  );
};

export default PrivacyPolicy;
